import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  pipes,
  settings,
  type DeclineReason,
  type PipeSummary,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

/**
 * Catalogo de motivos padronizados.
 *
 * Texto livre nao agrega: "cliente desistiu", "desistencia" e "Desistencia do
 * cliente" viram tres motivos diferentes no relatorio de subscricao. O catalogo
 * existe para que a mesma ideia tenha uma redacao so.
 *
 * O escopo separa onde cada motivo aparece. Um motivo de recusa de credito nao faz
 * sentido em um cancelamento de apolice, e misturar os dois obrigaria quem opera a
 * ignorar metade da lista.
 */

const escopos = [
  { value: 'credito', label: 'Recusa de credito' },
  { value: 'subscricao', label: 'Recusa de subscricao' },
  { value: 'juridico', label: 'Pendencia juridica' },
  { value: 'cancelamento', label: 'Cancelamento de apolice' },
  // As esteiras que o SeguraCore nao tinha (P-EST-1).
  { value: 'cadastro', label: 'Esteira de cadastro' },
  { value: 'nomeacao', label: 'Esteira de nomeacao' },
  { value: 'pep', label: 'Esteira PEP' },
  { value: 'resseguro', label: 'Esteira de resseguro' },
];

export function DeclineReasonsPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeVer = can('configuracoes.motivo.view');
  const podeCriar = can('configuracoes.motivo.create');
  const podeEditar = can('configuracoes.motivo.update');
  const podeRemover = can('configuracoes.motivo.delete');

  const [escopo, setEscopo] = useState('credito');
  const [criando, setCriando] = useState(false);
  const [removendo, setRemovendo] = useState<DeclineReason | null>(null);
  const [ligando, setLigando] = useState<DeclineReason | null>(null);

  // Em quais esteiras cada motivo vale (P-EST-1): um motivo vale para varias, como no
  // A07 do OnPoint.
  const podeVerEsteiras = can('esteiras.esteira.view');
  const esteiras = useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: podeVer && podeVerEsteiras,
  });
  const nomeDaEsteira = new Map((esteiras.data ?? []).map((e) => [e.id, e.name]));

  const consulta = useQuery({
    queryKey: ['decline-reasons', escopo],
    queryFn: ({ signal }) =>
      settings.declineReasons({ scope: escopo, include_inactive: true }, signal),
    enabled: podeVer,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['decline-reasons'] });
  }

  const criar = useMutation({
    mutationFn: ({ label, pipeIds }: { label: string; pipeIds?: string[] }) =>
      settings.createReason({
        scope: escopo,
        label,
        ...(pipeIds && pipeIds.length > 0 ? { pipe_ids: pipeIds } : {}),
      }),
    onSuccess: () => {
      toast.success('Motivo criado.');
      setCriando(false);
      invalidar();
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      settings.updateReason(id, { active }),
    onSuccess: (motivo) => {
      toast.success(motivo.active ? 'Motivo reativado.' : 'Motivo desativado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const ligar = useMutation({
    mutationFn: ({ id, pipeIds }: { id: string; pipeIds: string[] }) =>
      settings.updateReason(id, { pipe_ids: pipeIds }),
    onSuccess: () => {
      toast.success('Esteiras do motivo atualizadas.');
      setLigando(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const remover = useMutation({
    mutationFn: (id: string) => settings.removeReason(id),
    onSuccess: () => {
      toast.success('Motivo removido do catalogo.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  const motivos = consulta.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Motivos padronizados"
        description="Usados na recusa, na pendencia e no cancelamento."
        action={
          podeCriar ? (
            <Button size="sm" variant="secondary" onClick={() => setCriando(true)}>
              Novo motivo
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-3">
        <Select
          label="Escopo"
          className="h-9 max-w-xs"
          value={escopo}
          options={escopos}
          onChange={(evento) => setEscopo(evento.target.value)}
        />

        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os motivos">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : motivos.length === 0 ? (
          <p className="text-sm text-ink-500">
            Nenhum motivo neste escopo. Sem catalogo, a recusa volta a aceitar texto
            livre, e o relatorio deixa de agrupar.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {motivos.map((motivo) => (
              <li key={motivo.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={
                      motivo.active
                        ? 'truncate text-sm text-ink-800'
                        : 'truncate text-sm text-ink-400 line-through'
                    }
                  >
                    {motivo.label}
                  </span>
                  {podeVerEsteiras ? (
                    <span className="truncate text-2xs text-ink-500">
                      {(motivo.pipe_ids ?? []).length === 0
                        ? 'Nenhuma esteira'
                        : `Esteiras: ${(motivo.pipe_ids ?? [])
                            .map((id) => nomeDaEsteira.get(id) ?? 'esteira')
                            .join(', ')}`}
                    </span>
                  ) : null}
                </span>

                {!motivo.active ? <Badge tone="neutral">Inativo</Badge> : null}

                {podeEditar && podeVerEsteiras ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Esteiras de ${motivo.label}`}
                    onClick={() => setLigando(motivo)}
                  >
                    Esteiras
                  </Button>
                ) : null}
                {podeEditar ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={alternar.isPending}
                    onClick={() =>
                      alternar.mutate({ id: motivo.id, active: !motivo.active })
                    }
                  >
                    {motivo.active ? 'Desativar' : 'Reativar'}
                  </Button>
                ) : null}
                {podeRemover ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remover ${motivo.label}`}
                    onClick={() => setRemovendo(motivo)}
                  >
                    Remover
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        <p className="text-2xs text-ink-500">
          Desativar tira o motivo das listas novas e mantem o historico. Remover apaga
          do catalogo, e tambem nao altera o que ja foi decidido: a decisao guarda o
          texto, e nao um ponteiro para esta lista.
        </p>
      </CardBody>

      {criando ? (
        <FormularioDeMotivo
          escopo={escopos.find((item) => item.value === escopo)?.label ?? escopo}
          esteiras={podeVerEsteiras ? (esteiras.data ?? []) : []}
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(label, pipeIds) => criar.mutate({ label, pipeIds })}
        />
      ) : null}

      {ligando ? (
        <EsteirasDoMotivo
          motivo={ligando}
          esteiras={esteiras.data ?? []}
          submitting={ligar.isPending}
          onCancel={() => setLigando(null)}
          onSubmit={(pipeIds) => ligar.mutate({ id: ligando.id, pipeIds })}
        />
      ) : null}

      {removendo ? (
        <Modal
          open
          onClose={() => setRemovendo(null)}
          title="Remover motivo"
          description={removendo.label}
          footer={
            <>
              <Button variant="secondary" onClick={() => setRemovendo(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={remover.isPending}
                onClick={() => remover.mutate(removendo.id)}
              >
                Remover
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-700">
            O motivo sai do catalogo. As decisoes que ja o usaram continuam com o texto
            gravado, e nao mudam.
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}

function FormularioDeMotivo({
  escopo,
  esteiras,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  escopo: string;
  esteiras: PipeSummary[];
  submitting: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (label: string, pipeIds: string[]) => void;
}) {
  const [label, setLabel] = useState('');
  const [marcadas, setMarcadas] = useState<string[]>([]);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    onSubmit(label, marcadas);
  }

  return (
    <Modal open onClose={onCancel} title="Novo motivo" description={escopo}>
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {error.message}
          </Alert>
        ) : null}

        <Field
          label="Texto do motivo"
          required
          value={label}
          hint="Escreva como deve aparecer no relatorio."
          onChange={(evento) => setLabel(evento.target.value)}
        />

        {esteiras.length > 0 ? (
          <EscolhaDeEsteiras
            esteiras={esteiras}
            marcadas={marcadas}
            onChange={setMarcadas}
            dica="Sem esteira marcada, o motivo vale na esteira de mesmo nome do escopo."
          />
        ) : null}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting}>
            Criar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Esteiras em que o motivo vale, editadas de uma vez. */
function EsteirasDoMotivo({
  motivo,
  esteiras,
  submitting,
  onCancel,
  onSubmit,
}: {
  motivo: DeclineReason;
  esteiras: PipeSummary[];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (pipeIds: string[]) => void;
}) {
  const [marcadas, setMarcadas] = useState<string[]>(motivo.pipe_ids ?? []);
  return (
    <Modal
      open
      onClose={onCancel}
      title="Esteiras do motivo"
      description={motivo.label}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button loading={submitting} onClick={() => onSubmit(marcadas)}>
            Salvar
          </Button>
        </>
      }
    >
      <EscolhaDeEsteiras
        esteiras={esteiras}
        marcadas={marcadas}
        onChange={setMarcadas}
        dica="O motivo aparece na movimentacao do cartao das esteiras marcadas."
      />
    </Modal>
  );
}

function EscolhaDeEsteiras({
  esteiras,
  marcadas,
  onChange,
  dica,
}: {
  esteiras: PipeSummary[];
  marcadas: string[];
  onChange: (ids: string[]) => void;
  dica: string;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs font-medium text-ink-700">Esteiras</legend>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {esteiras.map((esteira) => (
          <label
            key={esteira.id}
            className="flex items-center gap-2 text-sm text-ink-700"
          >
            <input
              type="checkbox"
              checked={marcadas.includes(esteira.id)}
              onChange={(evento) =>
                onChange(
                  evento.target.checked
                    ? [...marcadas, esteira.id]
                    : marcadas.filter((id) => id !== esteira.id),
                )
              }
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            {esteira.name}
          </label>
        ))}
      </div>
      <p className="text-2xs text-ink-500">{dica}</p>
    </fieldset>
  );
}
